import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLogin } from '../hooks/useSWR';
import {
  syncUserUiPreferencesToLocal,
  normalizeUiPreferencesJson,
} from '../config/uiPreferences';

const AuthContext = createContext();

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

// Export as useAuth for backward compatibility
export const useAuth = useAuthContext;

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const { login: loginMutation, isLoading: isLoggingIn, error: loginError } = useLogin();
  // Note: No profile endpoint exists, so we'll use the stored user data
  // const { user: userData, isLoading: isLoadingUser, mutate: refreshUser } = useSWRAuth();

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
        syncUserUiPreferencesToLocal(parsedUser);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Keep React user in sync when appearance prefs are saved (ThemeSwitcher, sidebar toggles, …)
  useEffect(() => {
    const onPrefsApplied = () => {
      const savedUser = localStorage.getItem('user');
      if (!savedUser) return;
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        syncUserUiPreferencesToLocal(parsedUser);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('ui-preferences-applied', onPrefsApplied);
    return () => window.removeEventListener('ui-preferences-applied', onPrefsApplied);
  }, []);

  // Note: No profile endpoint exists, so we rely on stored user data
  // No need to fetch user data from server

  const login = async (credentials) => {
    try {
      const result = await loginMutation(credentials);
      
      if (result.access_token) {
        localStorage.setItem('authToken', result.access_token);
        const userWithPrefs = {
          ...result.user,
          uiPreferences: normalizeUiPreferencesJson(result.user?.uiPreferences),
        };
        localStorage.setItem('user', JSON.stringify(userWithPrefs));
        setUser(userWithPrefs);
        setIsAuthenticated(true);
        syncUserUiPreferencesToLocal(userWithPrefs);
        window.dispatchEvent(new CustomEvent('ui-preferences-applied'));
        return { success: true, user: userWithPrefs };
      }
      
      return { success: false, error: 'No access token received' };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.message || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    // Optionally call backend logout endpoint
    // fetch('/api/auth/logout', { method: 'POST' });
  };

  const value = {
    isAuthenticated,
    user,
    loading: loading || isLoggingIn,
    login,
    logout,
    updateUser: (partial) => {
      setUser((prev) => {
        const next = { ...(prev || {}), ...partial };
        try {
          localStorage.setItem('user', JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    refreshUser: () => {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          syncUserUiPreferencesToLocal(parsedUser);
          window.dispatchEvent(new CustomEvent('ui-preferences-applied'));
        } catch (error) {
          console.error('Error parsing saved user:', error);
        }
      }
    },
    loginError,
    isLoggingIn
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};